import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  UserPlus, Search, Edit3, Upload, FileText, ArrowRightCircle,
  Loader2, Users, Calendar, Star, Award, Briefcase, Trash2, Sparkles, AlertTriangle,
  Eye, LayoutGrid, List as ListIcon,
} from "lucide-react";

const STATUS_ORDER = [
  "yeni_basvuru", "gorusme_planlandi", "gorusuldu", "ikinci_gorusme",
  "teklif_verildi", "ise_alindi", "beklemede", "olumsuz",
];
const STATUS_LABEL = {
  yeni_basvuru: "Yeni Başvuru",
  gorusme_planlandi: "Görüşme Planlandı",
  gorusuldu: "Görüşüldü",
  ikinci_gorusme: "İkinci Görüşme",
  teklif_verildi: "Teklif Verildi",
  ise_alindi: "İşe Alındı",
  beklemede: "Beklemede",
  olumsuz: "Olumsuz",
};
const STATUS_TONE = {
  yeni_basvuru: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  gorusme_planlandi: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  gorusuldu: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  ikinci_gorusme: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  teklif_verildi: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  ise_alindi: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  beklemede: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  olumsuz: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const EMPTY = {
  name: "", phone: "", email: "", position: "", department: "",
  application_date: "", application_source: "", salary_expectation: "",
  interview_at: "", interviewer_user_id: "", interview_notes: "",
  next_interview_at: "", general_note: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return String(iso).slice(0, 10); }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR"); } catch { return String(iso).slice(0, 16); }
}

export default function Candidates() {
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState({ total: 0, waiting_interview: 0, today_interviews: 0, offered: 0, hired: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ start_date: "", role: "operator", department: "" });
  const [converting, setConverting] = useState(false);
  const [meta, setMeta] = useState({ roles: [], role_labels: {}, departments: [], department_labels: {} });
  const [uploadingCv, setUploadingCv] = useState(false);
  const [users, setUsers] = useState([]);
  // + Yeni Departman mini-dialog (ortak kaynak: /employees/meta/departments)
  const [deptAddOpen, setDeptAddOpen] = useState(false);
  const [deptName, setDeptName] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);
  const cvInputRef = useRef(null);
  // AI CV
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiDraft, setAiDraft] = useState(null); // {cv_url, cv_filename, form, cv_summary, ai_error}
  const [aiCreating, setAiCreating] = useState(false);
  const [aiDupe, setAiDupe] = useState(null); // {id,name,phone,email,status}
  const aiFileRef = useRef(null);
  // View mode: table | kanban
  const [viewMode, setViewMode] = useState("table");
  const [dragOverStatus, setDragOverStatus] = useState(null);
  // CV Preview
  const [cvPreviewOpen, setCvPreviewOpen] = useState(false);
  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null); // candidate object
  const [deleting, setDeleting] = useState(false);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const [{ data }, { data: k }] = await Promise.all([
        api.get("/candidates", { params }),
        api.get("/candidates/kpi"),
      ]);
      setRows(data || []);
      setKpi(k || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    // fetch employees meta (roles + departments) & users list once
    (async () => {
      try {
        const [{ data: m }, { data: u }] = await Promise.all([
          api.get("/employees/meta"),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        setMeta(m);
        setUsers(Array.isArray(u) ? u : []);
      } catch (err) { /* silent */ }
    })();
  }, []);

  const submitNewDepartment = async () => {
    const name = deptName.trim();
    if (!name) return;
    setDeptSaving(true);
    try {
      const { data } = await api.post("/employees/meta/departments", { name });
      const { data: m } = await api.get("/employees/meta");
      setMeta(m);
      setForm((f) => ({ ...f, department: data.code }));
      toast.success(data.existing ? "Mevcut departman seçildi" : "Departman eklendi ve seçildi");
      setDeptAddOpen(false);
      setDeptName("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeptSaving(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, application_date: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      position: c.position || "",
      department: c.department || "",
      application_date: (c.application_date || "").slice(0, 10),
      application_source: c.application_source || "",
      salary_expectation: c.salary_expectation ?? "",
      interview_at: (c.interview_at || "").slice(0, 16),
      interviewer_user_id: c.interviewer_user_id || "",
      interview_notes: c.interview_notes || "",
      next_interview_at: (c.next_interview_at || "").slice(0, 16),
      general_note: c.general_note || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Aday adı zorunlu"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        salary_expectation: form.salary_expectation === "" ? null : Number(form.salary_expectation),
        application_date: form.application_date || null,
        interview_at: form.interview_at || null,
        next_interview_at: form.next_interview_at || null,
      };
      if (editing) {
        await api.put(`/candidates/${editing.id}`, payload);
        toast.success("Aday güncellendi");
      } else {
        await api.post("/candidates", payload);
        toast.success("Aday oluşturuldu");
      }
      setDialogOpen(false);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const quickChangeStatus = async (candidate, newStatus) => {
    if (newStatus === candidate.status) return;
    try {
      await api.post(`/candidates/${candidate.id}/status`, { status: newStatus });
      toast.success(`Durum: ${STATUS_LABEL[newStatus]}`);
      loadList();
      if (detail?.id === candidate.id) {
        const { data } = await api.get("/candidates", { params: { search: candidate.name } });
        const fresh = (data || []).find((r) => r.id === candidate.id);
        if (fresh) setDetail(fresh);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadCv = async (candidate, file) => {
    if (!file) return;
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/candidates/${candidate.id}/cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("CV yüklendi");
      if (detail?.id === candidate.id) setDetail(data);
      loadList();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingCv(false); }
  };

  const startConvert = (candidate) => {
    setDetail(candidate);
    setConvertForm({
      start_date: new Date().toISOString().slice(0, 10),
      role: (meta.roles && meta.roles[0]) || "operator",
      department: candidate.department || "",
    });
    setConvertOpen(true);
  };

  const doConvert = async () => {
    if (!detail) return;
    if (!convertForm.start_date) { toast.error("İşe Başlama Tarihi zorunlu"); return; }
    if (!convertForm.role) { toast.error("Rol zorunlu"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/candidates/${detail.id}/convert-to-employee`, convertForm);
      toast.success(`Çalışan oluşturuldu: ${data.name}`);
      setConvertOpen(false);
      setDetail(null);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setConverting(false); }
  };

  const filtered = useMemo(() => rows, [rows]);

  // ===== AI CV Yükle =====
  const openAiDialog = () => {
    setAiDraft(null);
    setAiDupe(null);
    setAiDialogOpen(true);
    setTimeout(() => aiFileRef.current?.click(), 100);
  };
  const analyzeCv = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Dosya çok büyük (max 10MB)"); return; }
    setAiAnalyzing(true);
    setAiDupe(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/candidates/ai-parse-cv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const s = data.suggested || {};
      setAiDraft({
        cv_url: data.cv_url,
        cv_filename: data.cv_filename,
        cv_summary: data.cv_summary || "",
        ai_error: data.ai_error || null,
        form: {
          name: s.name || "",
          phone: s.phone || "",
          email: s.email || "",
          position: s.position || "",
          department: "",
          salary_expectation: "",
          general_note: data.cv_summary || "",
        },
      });
      if (data.ai_error) {
        toast.warning("CV yüklendi ancak AI analiz başarısız oldu. Alanları manuel doldurabilirsiniz.");
      } else {
        toast.success("CV analiz edildi. Lütfen bilgileri kontrol edin.");
      }
      // duplicate check
      if (s.phone || s.email) {
        try {
          const { data: dup } = await api.get("/candidates/find-duplicate", {
            params: { phone: s.phone || undefined, email: s.email || undefined },
          });
          if (dup?.match) setAiDupe(dup.match);
        } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiAnalyzing(false);
    }
  };
  const updateAiForm = (patch) => setAiDraft((d) => ({ ...d, form: { ...d.form, ...patch } }));

  const createFromAi = async () => {
    if (!aiDraft) return;
    if (!aiDraft.form.name.trim()) { toast.error("Ad Soyad zorunlu"); return; }
    // final duplicate re-check
    const { form } = aiDraft;
    if (form.phone || form.email) {
      try {
        const { data: dup } = await api.get("/candidates/find-duplicate", {
          params: { phone: form.phone || undefined, email: form.email || undefined },
        });
        if (dup?.match) {
          setAiDupe(dup.match);
          toast.error("Bu telefon/e-posta ile kayıtlı bir aday bulunuyor.");
          return;
        }
      } catch { /* silent */ }
    }
    setAiCreating(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || "",
        email: form.email || "",
        position: form.position || "",
        department: form.department || "",
        salary_expectation: form.salary_expectation === "" ? null : Number(form.salary_expectation),
        general_note: form.general_note || "",
        application_date: new Date().toISOString().slice(0, 10),
        cv_url: aiDraft.cv_url || "",
        cv_filename: aiDraft.cv_filename || "",
      };
      await api.post("/candidates", payload);
      toast.success("Aday oluşturuldu (CV bağlandı)");
      setAiDialogOpen(false);
      setAiDraft(null);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setAiCreating(false); }
  };

  const openExistingFromDupe = () => {
    if (!aiDupe) return;
    // list should have it — find & open detail
    const c = rows.find((r) => r.id === aiDupe.id);
    setAiDialogOpen(false);
    if (c) setDetail(c);
    else toast.info("Aday listede bulunmuyor, aramayı temizleyip tekrar deneyin.");
  };

  // ===== Delete =====
  const doDelete = async (force) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/candidates/${deleteTarget.id}`, { params: force ? { force: true } : {} });
      toast.success("Aday silindi");
      setDeleteTarget(null);
      if (detail?.id === deleteTarget.id) setDetail(null);
      loadList();
    } catch (err) {
      // 409 → offer force option in same dialog by re-throwing to keep open
      const msg = formatApiError(err);
      toast.error(msg);
    } finally { setDeleting(false); }
  };

  // ===== Kanban drag-drop =====
  const onDragStart = (e, candidate) => {
    e.dataTransfer.setData("text/plain", candidate.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStatus !== status) setDragOverStatus(status);
  };
  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverStatus(null);
    const id = e.dataTransfer.getData("text/plain");
    const c = rows.find((r) => r.id === id);
    if (!c || c.status === newStatus) return;
    // optimistic update
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
    try {
      await api.post(`/candidates/${id}/status`, { status: newStatus });
      toast.success(`${c.name}: ${STATUS_LABEL[newStatus]}`);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
      loadList();
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="candidates-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <UserPlus className="w-7 h-7 text-emerald-400" /> İşe Alım / Aday Takip
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aday başvurularını, görüşmeleri ve işe alım sürecini tek ekrandan yönetin.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openAiDialog}
            data-testid="ai-cv-upload-btn"
            variant="outline"
            className="border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI İle CV Yükle
          </Button>
          <Button onClick={openNew} data-testid="new-candidate-btn" className="bg-emerald-600 hover:bg-emerald-500">
            <UserPlus className="w-4 h-4 mr-2" /> Yeni Aday
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "total", label: "Toplam Aday", val: kpi.total, tone: "text-sky-300", icon: Users },
          { key: "waiting_interview", label: "Görüşme Planlandı", val: kpi.waiting_interview, tone: "text-indigo-300", icon: Calendar },
          { key: "today_interviews", label: "Bugünkü Görüşme", val: kpi.today_interviews, tone: "text-fuchsia-300", icon: Star },
          { key: "offered", label: "Teklif Verildi", val: kpi.offered, tone: "text-amber-300", icon: Award },
          { key: "hired", label: "İşe Alındı", val: kpi.hired, tone: "text-emerald-300", icon: Briefcase },
        ].map(({ key, label, val, tone, icon: Icon }) => (
          <Card key={key} className="p-4 flex items-center gap-3" data-testid={`kpi-${key}`}>
            <Icon className={`w-6 h-6 ${tone}`} />
            <div>
              <div className={`text-2xl font-semibold ${tone}`}>{val ?? 0}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="candidate-search"
            className="pl-8 w-64"
            placeholder="Ara: isim, telefon, email, pozisyon"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            {STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        <div className="ml-auto flex items-center gap-1 border rounded p-0.5">
          <Button
            size="sm"
            variant={viewMode === "table" ? "default" : "ghost"}
            className="h-7"
            onClick={() => setViewMode("table")}
            data-testid="view-table-btn"
          >
            <ListIcon className="w-3.5 h-3.5 mr-1" /> Tablo
          </Button>
          <Button
            size="sm"
            variant={viewMode === "kanban" ? "default" : "ghost"}
            className="h-7"
            onClick={() => setViewMode("kanban")}
            data-testid="view-kanban-btn"
          >
            <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Kanban
          </Button>
        </div>
      </Card>

      {viewMode === "kanban" ? (
        <div className="overflow-x-auto pb-3" data-testid="kanban-board">
          <div className="flex gap-3 min-w-max">
            {STATUS_ORDER.map((s) => {
              const cards = rows.filter((r) => r.status === s);
              const isOver = dragOverStatus === s;
              return (
                <div
                  key={s}
                  onDragOver={(e) => onDragOver(e, s)}
                  onDragLeave={() => setDragOverStatus(null)}
                  onDrop={(e) => onDrop(e, s)}
                  className={`w-72 rounded-lg border ${isOver ? "border-emerald-400 bg-emerald-500/5" : "border-border/50 bg-card/40"} p-2 flex flex-col`}
                  data-testid={`kanban-col-${s}`}
                >
                  <div className="flex items-center justify-between px-1 py-1.5 mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_TONE[s]}>{STATUS_LABEL[s]}</Badge>
                      <span className="text-xs text-muted-foreground">{cards.length}</span>
                    </div>
                  </div>
                  <div className="space-y-2 min-h-[80px]">
                    {cards.map((c) => (
                      <div
                        key={c.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, c)}
                        onClick={() => setDetail(c)}
                        className="rounded-md bg-background border p-2.5 cursor-grab active:cursor-grabbing hover:border-emerald-500/50 transition"
                        data-testid={`kanban-card-${c.id}`}
                      >
                        <div className="font-medium text-sm truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.position || "—"}
                          {c.department ? ` · ${meta.department_labels?.[c.department] || c.department}` : ""}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          {c.phone && <span>{c.phone}</span>}
                          {c.cv_url && <FileText className="w-3 h-3 text-emerald-400" />}
                          {c.converted_employee_id && <span className="text-emerald-400">✓</span>}
                        </div>
                      </div>
                    ))}
                    {cards.length === 0 && (
                      <div className="text-xs text-muted-foreground/70 text-center py-4">
                        Bu duruma sürükleyin
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
      /* Table */
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Aday</TableHead>
              <TableHead>Pozisyon / Departman</TableHead>
              <TableHead>İletişim</TableHead>
              <TableHead>Başvuru</TableHead>
              <TableHead>Görüşme</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead className="w-[240px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Henüz aday yok. "Yeni Aday" ile başlayın.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`candidate-row-${c.id}`}>
                <TableCell>
                  <button
                    className="font-medium hover:underline text-left"
                    onClick={() => setDetail(c)}
                    data-testid={`candidate-open-${c.id}`}
                  >
                    {c.name}
                  </button>
                  {c.converted_employee_id && (
                    <div className="text-xs text-emerald-400 mt-0.5">✓ Çalışana dönüştürüldü</div>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  <div>{c.position || "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.department ? (meta.department_labels?.[c.department] || c.department) : "—"}
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{c.phone || "—"}</div>
                  <div className="text-xs text-muted-foreground">{c.email || "—"}</div>
                </TableCell>
                <TableCell className="text-sm">{fmtDate(c.application_date)}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(c.interview_at)}</TableCell>
                <TableCell>
                  <Select value={c.status} onValueChange={(v) => quickChangeStatus(c, v)}>
                    <SelectTrigger className="w-[170px] h-8" data-testid={`status-${c.id}`}>
                      <SelectValue>
                        <Badge variant="outline" className={STATUS_TONE[c.status]}>
                          {STATUS_LABEL[c.status]}
                        </Badge>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_ORDER.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDetail(c)}
                      data-testid={`detail-${c.id}`}
                    >
                      Detay
                    </Button>
                    {!c.converted_employee_id && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 h-8"
                        onClick={() => startConvert(c)}
                        data-testid={`convert-${c.id}`}
                      >
                        <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />
                        Çalışana Dönüştür
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      )}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="candidate-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Aday Düzenle" : "Yeni Aday"}</DialogTitle>
            <DialogDescription>
              {editing ? "Aday bilgilerini güncelleyin." : "Aday başvurusunu sisteme ekleyin."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Ad Soyad *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="candidate-name" />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="candidate-phone" />
            </div>
            <div>
              <Label>E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="candidate-email" />
            </div>
            <div>
              <Label>Pozisyon</Label>
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="candidate-position" />
            </div>
            <div>
              <Label>Departman</Label>
              <Select value={form.department || ""} onValueChange={(v) => {
                if (v === "__new__") { setDeptName(""); setDeptAddOpen(true); return; }
                setForm({ ...form, department: v === "__none" ? "" : v });
              }}>
                <SelectTrigger data-testid="candidate-dept"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Yok —</SelectItem>
                  {(meta.departments || []).map((d) => (
                    <SelectItem key={d} value={d}>{meta.department_labels?.[d] || d}</SelectItem>
                  ))}
                  <SelectItem value="__new__" data-testid="candidate-dept-new-opt" className="text-cyan-400 font-semibold">+ Yeni Departman Ekle</SelectItem>
                </SelectContent>
              </Select>
              {/* + Yeni Departman mini-modal */}
              <Dialog open={deptAddOpen} onOpenChange={(v) => !v && setDeptAddOpen(false)}>
                <DialogContent className="max-w-sm" data-testid="candidate-dept-dialog">
                  <DialogHeader>
                    <DialogTitle>Yeni Departman</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label>Departman Adı</Label>
                    <Input
                      autoFocus
                      data-testid="candidate-dept-name"
                      value={deptName}
                      onChange={(e) => setDeptName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNewDepartment(); } }}
                      placeholder="Örn. Ar-Ge"
                    />
                  </div>
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setDeptAddOpen(false)} data-testid="candidate-dept-cancel">İptal</Button>
                    <Button type="button" onClick={submitNewDepartment} disabled={deptSaving || !deptName.trim()} data-testid="candidate-dept-save">
                      {deptSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Kaydet"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div>
              <Label>Başvuru Tarihi</Label>
              <Input type="date" value={form.application_date || ""} onChange={(e) => setForm({ ...form, application_date: e.target.value })} />
            </div>
            <div>
              <Label>Başvuru Kaynağı</Label>
              <Input placeholder="Linkedin, Kariyer.net, Referans..." value={form.application_source} onChange={(e) => setForm({ ...form, application_source: e.target.value })} />
            </div>
            <div>
              <Label>Maaş Beklentisi (₺)</Label>
              <Input type="number" value={form.salary_expectation} onChange={(e) => setForm({ ...form, salary_expectation: e.target.value })} />
            </div>
            <div>
              <Label>Görüşme Tarihi</Label>
              <Input type="datetime-local" value={form.interview_at} onChange={(e) => setForm({ ...form, interview_at: e.target.value })} />
            </div>
            <div>
              <Label>Görüşme Yapan</Label>
              <Select value={form.interviewer_user_id || ""} onValueChange={(v) => setForm({ ...form, interviewer_user_id: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Yok —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sonraki Görüşme</Label>
              <Input type="datetime-local" value={form.next_interview_at} onChange={(e) => setForm({ ...form, next_interview_at: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Görüşme Notları</Label>
              <Textarea rows={3} value={form.interview_notes} onChange={(e) => setForm({ ...form, interview_notes: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Genel Not</Label>
              <Textarea rows={2} value={form.general_note} onChange={(e) => setForm({ ...form, general_note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Vazgeç</Button>
            <Button onClick={submit} disabled={saving} data-testid="candidate-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detail && !convertOpen} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="candidate-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.name}
                  <Badge variant="outline" className={STATUS_TONE[detail.status]}>
                    {STATUS_LABEL[detail.status]}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {detail.position || "—"} • {detail.department ? (meta.department_labels?.[detail.department] || detail.department) : "—"}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Telefon</div>
                  <div>{detail.phone || "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">E-posta</div>
                  <div>{detail.email || "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Başvuru Tarihi</div>
                  <div>{fmtDate(detail.application_date)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Kaynak</div>
                  <div>{detail.application_source || "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Maaş Beklentisi</div>
                  <div>{detail.salary_expectation ? `₺${Number(detail.salary_expectation).toLocaleString("tr-TR")}` : "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Görüşme</div>
                  <div>{fmtDateTime(detail.interview_at)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Görüşme Notları</div>
                  <div className="whitespace-pre-wrap">{detail.interview_notes || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Genel Not</div>
                  <div className="whitespace-pre-wrap">{detail.general_note || "—"}</div>
                </div>
              </div>

              {/* CV Section */}
              <Card className="p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">CV</span>
                    {detail.cv_filename ? (
                      <span className="text-sm text-emerald-400">{detail.cv_filename}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">Yüklenmemiş</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <input
                      ref={cvInputRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      data-testid="cv-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadCv(detail, f);
                        e.target.value = "";
                      }}
                    />
                    {detail.cv_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCvPreviewOpen(true)}
                        data-testid="cv-preview-btn"
                      >
                        <Eye className="w-4 h-4 mr-1" /> Önizle
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={uploadingCv}
                      onClick={() => cvInputRef.current?.click()}
                      data-testid="cv-upload-btn"
                    >
                      {uploadingCv ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {detail.cv_url ? "CV Değiştir" : "CV Yükle"}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Status History */}
              <Card className="p-3">
                <div className="font-medium mb-2">Durum Geçmişi</div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {(detail.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-l-2 border-emerald-500/40 pl-3">
                      <div>
                        <Badge variant="outline" className={STATUS_TONE[h.status]}>{STATUS_LABEL[h.status]}</Badge>
                        {h.note && <span className="ml-2 text-muted-foreground">{h.note}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmtDateTime(h.at)} • {h.by || "—"}
                      </div>
                    </div>
                  ))}
                  {(detail.status_history || []).length === 0 && (
                    <div className="text-sm text-muted-foreground">Geçmiş yok.</div>
                  )}
                </div>
              </Card>
              </div>

              <DialogFooter className="flex flex-wrap gap-2 sm:justify-between shrink-0">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setDetail(null); openEdit(detail); }} data-testid="detail-edit-btn">
                    <Edit3 className="w-4 h-4 mr-1" /> Düzenle
                  </Button>
                  <Button
                    variant="outline"
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    onClick={() => setDeleteTarget(detail)}
                    data-testid="detail-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Sil
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDetail(null)}>Kapat</Button>
                  {!detail.converted_employee_id && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-500"
                      onClick={() => startConvert(detail)}
                      data-testid="detail-convert-btn"
                    >
                      <ArrowRightCircle className="w-4 h-4 mr-1" /> Çalışana Dönüştür
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CONVERT DIALOG */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Çalışana Dönüştür</DialogTitle>
            <DialogDescription>
              {detail?.name} adayı çalışan kartına aktarılacak. Aday "İşe Alındı" olarak işaretlenecek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>İşe Başlama Tarihi *</Label>
              <Input
                type="date"
                value={convertForm.start_date}
                onChange={(e) => setConvertForm({ ...convertForm, start_date: e.target.value })}
                data-testid="convert-start-date"
              />
            </div>
            <div>
              <Label>Rol / Pozisyon *</Label>
              <Select value={convertForm.role} onValueChange={(v) => setConvertForm({ ...convertForm, role: v })}>
                <SelectTrigger data-testid="convert-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.roles || []).map((r) => (
                    <SelectItem key={r} value={r}>{meta.role_labels?.[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Departman</Label>
              <Select value={convertForm.department || "__none"} onValueChange={(v) => setConvertForm({ ...convertForm, department: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="convert-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Yok —</SelectItem>
                  {(meta.departments || []).map((d) => (
                    <SelectItem key={d} value={d}>{meta.department_labels?.[d] || d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Vazgeç</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500"
              onClick={doConvert}
              disabled={converting}
              data-testid="convert-confirm"
            >
              {converting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Onayla ve Çalışan Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* AI CV DIALOG */}
      <Dialog open={aiDialogOpen} onOpenChange={(v) => { if (!v) { setAiDialogOpen(false); setAiDraft(null); setAiDupe(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="ai-cv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-400" /> AI ile CV'den Aday Oluştur
            </DialogTitle>
            <DialogDescription>
              CV'yi yükleyin. AI okuyup önerileri gösterecek. Aday ONAYINIZLA oluşturulur.
              PDF · DOCX · JPG · PNG · max 10MB
            </DialogDescription>
          </DialogHeader>

          <input
            ref={aiFileRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            data-testid="ai-cv-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) analyzeCv(f);
              e.target.value = "";
            }}
          />

          {aiAnalyzing && (
            <div className="py-8 text-center space-y-2" data-testid="ai-analyzing">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-fuchsia-400" />
              <div className="text-sm text-muted-foreground">
                CV analiz ediliyor... Bu birkaç saniye sürebilir.
              </div>
            </div>
          )}

          {!aiAnalyzing && !aiDraft && (
            <div className="py-8 text-center space-y-3">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50" />
              <Button
                onClick={() => aiFileRef.current?.click()}
                className="bg-fuchsia-600 hover:bg-fuchsia-500"
                data-testid="ai-cv-select-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> CV Dosyası Seç
              </Button>
              <p className="text-xs text-muted-foreground">
                Kabul edilen formatlar: .pdf, .docx, .jpg, .png (maks. 10MB)
              </p>
            </div>
          )}

          {!aiAnalyzing && aiDraft && (
            <div className="space-y-3">
              {aiDraft.ai_error && (
                <div className="p-3 rounded border border-amber-500/40 bg-amber-500/10 text-sm text-amber-300 flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                  <div>
                    AI analiz başarısız oldu ({aiDraft.ai_error}). CV yüklendi — alanları manuel doldurup adayı oluşturabilirsiniz.
                  </div>
                </div>
              )}

              {aiDupe && (
                <div className="p-3 rounded border border-rose-500/40 bg-rose-500/10 text-sm space-y-2" data-testid="ai-duplicate-warning">
                  <div className="flex items-center gap-2 text-rose-300 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    Bu telefon/e-posta ile kayıtlı bir aday bulunuyor.
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {aiDupe.name} · {aiDupe.phone || "—"} · {aiDupe.email || "—"} · durum: {STATUS_LABEL[aiDupe.status] || aiDupe.status}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={openExistingFromDupe} data-testid="ai-open-existing">
                      Mevcut Adayı Aç
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setAiDialogOpen(false); setAiDraft(null); setAiDupe(null); }} data-testid="ai-cancel-dupe">
                      İptal
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-xs text-fuchsia-300 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> {aiDraft.cv_filename}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Ad Soyad *</Label>
                  <Input
                    value={aiDraft.form.name}
                    onChange={(e) => updateAiForm({ name: e.target.value })}
                    data-testid="ai-form-name"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input value={aiDraft.form.phone} onChange={(e) => updateAiForm({ phone: e.target.value })} data-testid="ai-form-phone" />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input value={aiDraft.form.email} onChange={(e) => updateAiForm({ email: e.target.value })} data-testid="ai-form-email" />
                </div>
                <div>
                  <Label>Pozisyon</Label>
                  <Input value={aiDraft.form.position} onChange={(e) => updateAiForm({ position: e.target.value })} data-testid="ai-form-position" />
                </div>
                <div>
                  <Label>Departman</Label>
                  <Select value={aiDraft.form.department || "__none"} onValueChange={(v) => updateAiForm({ department: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="ai-form-dept"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Yok —</SelectItem>
                      {(meta.departments || []).map((d) => (
                        <SelectItem key={d} value={d}>{meta.department_labels?.[d] || d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Maaş Beklentisi (₺)</Label>
                  <Input
                    type="number"
                    value={aiDraft.form.salary_expectation}
                    onChange={(e) => updateAiForm({ salary_expectation: e.target.value })}
                    data-testid="ai-form-salary"
                  />
                </div>
                <div className="col-span-2">
                  <Label>CV Özeti</Label>
                  <Textarea
                    rows={6}
                    value={aiDraft.form.general_note}
                    onChange={(e) => updateAiForm({ general_note: e.target.value })}
                    data-testid="ai-form-summary"
                    placeholder="Son firma, tecrübe, eğitim, yabancı dil, ehliyet, yetkinlikler..."
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAiDialogOpen(false); setAiDraft(null); setAiDupe(null); }}>
              Vazgeç
            </Button>
            {aiDraft && (
              <Button
                onClick={createFromAi}
                disabled={aiCreating || !!aiDupe}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="ai-create-btn"
              >
                {aiCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adayı Oluştur
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CV PREVIEW DIALOG */}
      <Dialog open={cvPreviewOpen} onOpenChange={setCvPreviewOpen}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 gap-0 flex flex-col" data-testid="cv-preview-dialog">
          <DialogHeader className="p-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="w-4 h-4" /> CV Önizleme — {detail?.cv_filename || "CV"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-slate-900/40">
            {detail?.cv_url && cvPreviewOpen && (
              <iframe
                key={detail.id}
                title="cv-preview"
                src={`${API_BASE}/candidates/${detail.id}/cv-file`}
                className="w-full h-full border-0"
                data-testid="cv-preview-frame"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Adayı sil</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{deleteTarget?.name}</b> adlı aday silinecek.
              {deleteTarget?.converted_employee_id ? (
                <span className="block mt-2 text-amber-400">
                  Bu aday çalışana dönüştürülmüş. Çalışan kaydı KORUNUR — sadece aday kaydı silinir.
                </span>
              ) : (
                <span className="block mt-2 text-muted-foreground">Bu işlem geri alınamaz.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-500"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); doDelete(!!deleteTarget?.converted_employee_id); }}
              data-testid="delete-confirm"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {deleteTarget?.converted_employee_id ? "Yine de sil" : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
